import { useEffect, useState } from "react";
import { Check, Crown, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PageHeader, Spinner } from "../components/common";
import { api, brl, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PERKS = {
  free: ["20 créditos de IA/mês", "Até 20 produtos", "Até 10 links com QR", "Painel de desempenho"],
  pro: ["300 créditos de IA/mês", "Produtos e links ilimitados", "Campanhas, metas e calendário", "Integrações oficiais"],
  premium: ["1000 créditos de IA/mês", "Tudo do Pro", "Prioridade em novos recursos", "Suporte prioritário"],
};

export default function Plans() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [credits, setCredits] = useState(null);
  const load = () => { api.get("/plans").then((r) => setPlans(r.data)); api.get("/credits").then((r) => setCredits(r.data)); };
  useEffect(load, []);

  const select = async (id) => {
    try { await api.post("/plans/select", { plan: id }); toast.success("Plano atualizado"); refreshUser(); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!plans || !credits) return <Spinner />;
  const isAdmin = user.role === "admin";

  return (
    <div>
      <PageHeader eyebrow="Assinatura" title="Planos & créditos" desc="Créditos de IA renovam todo mês conforme o seu plano. 1 geração = 1 crédito." />
      {!plans.billing_enabled && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200 flex gap-2 mb-6" data-testid="billing-disabled-banner">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />Cobrança real desativada. Os planos pagos serão liberados após a configuração de um provedor de pagamento. Nenhum valor será cobrado.
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {plans.plans.map((p) => {
          const current = user.plan === p.id, hi = p.id === "pro";
          const can = p.id === "free" || isAdmin || plans.billing_enabled;
          return (
            <div key={p.id} className={`rounded-2xl p-6 border flex flex-col rise ${hi ? "border-amber-500/50 bg-gradient-to-b from-amber-500/10 to-zinc-900/60" : "border-zinc-800 bg-zinc-900/60"}`} data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center justify-between"><div className="font-display text-xl font-extrabold">{p.name}</div>{hi && <span className="badge-gold"><Crown className="h-3 w-3" />Popular</span>}</div>
              <div className="mt-4"><span className="font-display text-4xl font-extrabold gold-text">{p.price ? brl(p.price) : "R$ 0"}</span><span className="text-xs text-zinc-500">/mês</span></div>
              <ul className="mt-5 space-y-2.5 flex-1">{PERKS[p.id].map((t) => <li key={t} className="flex gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 text-amber-400 shrink-0" />{t}</li>)}</ul>
              <Button disabled={current || !can} onClick={() => select(p.id)} className={`mt-6 rounded-full h-11 ${current ? "bg-zinc-800 text-zinc-400" : "btn-gold"}`} data-testid={`plan-select-${p.id}`}>
                {current ? "Plano atual" : can ? (isAdmin && p.id !== "free" ? "Ativar (modo teste admin)" : "Escolher") : <><Lock className="h-4 w-4 mr-1" />Em breve</>}
              </Button>
            </div>
          );
        })}
      </div>
      <div className="card-lux p-5 mt-6">
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold">Histórico de créditos</h3><span className="badge-gold" data-testid="plans-credit-balance">Saldo: {credits.credits}</span></div>
        {credits.history.length === 0 ? <p className="text-sm text-zinc-500">Sem movimentações.</p> : credits.history.map((h) => (
          <div key={h.id} className="flex justify-between py-2 border-b border-zinc-800/70 last:border-0 text-sm">
            <span className="text-zinc-300">{h.reason}</span>
            <span className={`font-mono ${h.amount > 0 ? "text-emerald-400" : "text-rose-400"}`}>{h.amount > 0 ? "+" : ""}{h.amount} <span className="text-[10px] text-zinc-500 ml-2">{new Date(h.created_at).toLocaleDateString("pt-BR")}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}
